import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useDrive } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Receipt,
  FileText,
  Link2,
  Calculator,
  BarChart3,
  Upload,
  AlertCircle,
  CheckCircle2,
  LogOut,
  Menu,
  X,
  Cloud,
  ArrowRight,
  Settings,
  Building2,
  ChevronDown,
  Shield,
  Users
} from "lucide-react";

// Sidebar Component
export const Sidebar = ({ activeItem }) => {
  const { user, logout } = useAuth();
  const { initialized, isAdmin, businesses, currentBusiness, selectBusiness } = useDrive();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const allNavItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3, path: "/dashboard" },
    { id: "receipts", label: "Receipts", icon: Receipt, path: "/receipts" },
    { id: "bank-statements", label: "Bank Statements", icon: FileText, path: "/bank-statements", adminOnly: true },
    { id: "matching", label: "Matching", icon: Link2, path: "/matching", adminOnly: true },
    { id: "vat-reports", label: "VAT Reports", icon: Calculator, path: "/vat-reports", adminOnly: true },
    { id: "reports", label: "Reports", icon: BarChart3, path: "/reports", adminOnly: true },
    { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
  ];

  const navItems = allNavItems.filter(item => !item.adminOnly || isAdmin);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleBusinessChange = (businessId) => {
    const business = businesses.find(b => b.id === businessId);
    if (business) {
      selectBusiness(business);
    }
  };

  const NavContent = () => (
    <>
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
            <Receipt className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Accruey
          </span>
        </div>
        
        {/* Business Selector */}
        {initialized && businesses.length > 0 && (
          <div className="mt-4">
            <Select 
              value={currentBusiness?.id || ""} 
              onValueChange={handleBusinessChange}
            >
              <SelectTrigger className="w-full bg-slate-50" data-testid="business-selector">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <SelectValue placeholder="Select business" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {businesses.map((business) => (
                  <SelectItem key={business.id} value={business.id}>
                    {business.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Drive Status */}
        <div className={`mt-3 flex items-center gap-2 text-xs ${initialized ? 'text-emerald-600' : 'text-amber-600'}`}>
          <Cloud className="w-4 h-4" />
          {initialized ? "Google Drive Connected" : "Initializing Drive..."}
          {isAdmin && <span className="ml-1 text-purple-600">(Admin)</span>}
        </div>
      </div>

      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                data-testid={`nav-${item.id}`}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                  activeItem === item.id
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 px-4 py-2 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
              <span className="text-slate-600 font-medium">{user?.name?.charAt(0) || "U"}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-sm text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          data-testid="logout-btn"
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
            <Receipt className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-slate-900">Accruey</span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-30">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-14 left-0 bottom-0 w-72 bg-white flex flex-col">
            <NavContent />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 flex-col z-30">
        <NavContent />
      </aside>
    </>
  );
};

// Main Dashboard Component
export default function Dashboard() {
  const { user, logout } = useAuth();
  const { 
    initialized, 
    initError, 
    retryInit, 
    isAdmin, 
    businesses, 
    currentBusiness, 
    folderIds, 
    listFiles, 
    getFileAsJson, 
    getMonthFolder,
    needsRoleSelection,
    selectingRole,
    setupAsAdmin,
    continueAsStaff
  } = useDrive();
  const navigate = useNavigate();

  // Redirect super_admin to their dashboard
  useEffect(() => {
    if (user?.global_role === "super_admin") {
      navigate("/super-admin", { replace: true });
    }
  }, [user, navigate]);

  const [stats, setStats] = useState({
    totalReceipts: 0,
    unmatchedReceipts: 0,
    totalTransactions: 0,
    unmatchedTransactions: 0,
    monthVat: 0,
    monthSpend: 0,
    monthReceiptsCount: 0
  });
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (initialized && currentBusiness) {
      if (folderIds.receipts) {
        loadDashboardData();
      } else {
        // No Drive folders available (e.g., staff without Drive access) — stop loading
        setLoading(false);
      }
    } else if (initialized && !currentBusiness) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, folderIds, currentBusiness]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get current month
      const now = new Date();
      const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      // List all month folders in receipts
      const monthFolders = await listFiles(folderIds.receipts, 'application/vnd.google-apps.folder');
      
      let totalReceipts = 0;
      let unmatchedReceipts = 0;
      let monthReceipts = [];
      
      // Load receipts from each month folder
      for (const folder of monthFolders) {
        const jsonFiles = await listFiles(folder.id);
        const receiptJsons = jsonFiles.filter(f => f.name.endsWith('.json'));
        
        for (const jsonFile of receiptJsons) {
          try {
            const receipt = await getFileAsJson(jsonFile.id);
            totalReceipts++;
            
            if (!receipt.isMatched) {
              unmatchedReceipts++;
            }
            
            if (folder.name === yearMonth) {
              monthReceipts.push({ ...receipt, fileId: jsonFile.id });
            }
          } catch (e) {
            console.error("Error loading receipt:", e);
          }
        }
      }
      
      // Calculate month stats
      const monthVat = monthReceipts.reduce((sum, r) => sum + (r.vatAmount || 0), 0);
      const monthSpend = monthReceipts.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
      
      // Load bank statements count
      let totalTransactions = 0;
      let unmatchedTransactions = 0;
      
      const statementFolders = await listFiles(folderIds.bank_statements);
      for (const file of statementFolders) {
        if (file.name.endsWith('.json')) {
          try {
            const statement = await getFileAsJson(file.id);
            if (statement.transactions) {
              totalTransactions += statement.transactions.length;
              unmatchedTransactions += statement.transactions.filter(t => !t.isMatched).length;
            }
          } catch (e) {
            console.error("Error loading statement:", e);
          }
        }
      }
      
      setStats({
        totalReceipts,
        unmatchedReceipts,
        totalTransactions,
        unmatchedTransactions,
        monthVat,
        monthSpend,
        monthReceiptsCount: monthReceipts.length
      });
      
      // Get recent receipts (last 5)
      setRecentReceipts(monthReceipts.slice(0, 5));
      
    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const currentMonth = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  
  const handleLogoutAndRetry = () => {
    logout();
    window.location.href = "/";
  };

  // Show error state if Drive initialization failed
  if (initError) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="dashboard" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-lg p-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Connection Failed</h2>
            <p className="text-slate-600 mb-4">{initError}</p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-6">
              <p className="font-medium text-amber-800 mb-2">To fix this:</p>
              <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                <li>Go to <a href="https://console.cloud.google.com/apis/library/drive.googleapis.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                <li>Enable the <strong>Google Drive API</strong></li>
                <li>Log out and sign in again to grant permission</li>
              </ol>
            </div>
            
            <div className="flex gap-2 justify-center">
              <Button onClick={retryInit}>
                Try Again
              </Button>
              <Button variant="outline" onClick={handleLogoutAndRetry}>
                Log Out & Retry
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!initialized) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="dashboard" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Cloud className="w-12 h-12 text-slate-400 mx-auto mb-4 animate-pulse" />
            <p className="text-slate-600">Connecting to Google Drive...</p>
            <p className="text-sm text-slate-400 mt-2">Setting up your folders...</p>
          </div>
        </main>
      </div>
    );
  }

  // Role Selection Screen for new users
  if (needsRoleSelection) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome to Accruey
            </h1>
            <p className="text-slate-600">How will you be using this application?</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Admin Option */}
            <div 
              className="bg-white rounded-xl border-2 border-slate-200 p-6 hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => !selectingRole && setupAsAdmin()}
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Admin</h3>
              <p className="text-slate-600 text-sm mb-4">
                I'm a business owner or manager who needs to set up businesses and manage staff access.
              </p>
              <ul className="text-sm text-slate-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Create multiple businesses
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Add staff and manage access
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Full control over data
                </li>
              </ul>
              <Button 
                className="w-full mt-6 bg-purple-600 hover:bg-purple-700" 
                disabled={selectingRole}
                onClick={(e) => {
                  e.stopPropagation();
                  setupAsAdmin();
                }}
              >
                {selectingRole ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Setting up...
                  </>
                ) : (
                  "Continue as Admin"
                )}
              </Button>
            </div>

            {/* Staff Option */}
            <div 
              className="bg-white rounded-xl border-2 border-slate-200 p-6 hover:border-blue-400 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => !selectingRole && continueAsStaff()}
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Staff / User</h3>
              <p className="text-slate-600 text-sm mb-4">
                I'm a team member who needs to upload receipts for businesses I've been given access to.
              </p>
              <ul className="text-sm text-slate-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Upload receipts
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Access shared businesses
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  View assigned data only
                </li>
              </ul>
              <Button 
                variant="outline"
                className="w-full mt-6" 
                disabled={selectingRole}
                onClick={(e) => {
                  e.stopPropagation();
                  continueAsStaff();
                }}
              >
                Continue as Staff
              </Button>
            </div>
          </div>

          <p className="text-center text-sm text-slate-400 mt-6">
            Logged in as <strong>{user?.email}</strong> • <button onClick={logout} className="text-blue-600 hover:underline">Sign out</button>
          </p>
        </div>
      </div>
    );
  }

  // No business selected - prompt to create or select one
  if (!currentBusiness) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="dashboard" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md p-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {isAdmin ? "Create Your First Business" : "No Business Access"}
            </h2>
            <p className="text-slate-600 mb-6">
              {isAdmin 
                ? "Get started by adding your first business in Settings. You can manage multiple businesses and share them with staff."
                : "You don't have access to any businesses yet. Please ask your administrator to share a business folder with your Google account."}
            </p>
            {isAdmin ? (
              <Button onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4 mr-2" />
                Go to Settings
              </Button>
            ) : (
              <>
                <div className="bg-slate-100 rounded-lg p-4 text-left mb-4">
                  <p className="text-sm text-slate-600 font-medium mb-2">What to do:</p>
                  <ol className="text-sm text-slate-500 list-decimal list-inside space-y-1">
                    <li>Contact your administrator</li>
                    <li>Provide your email: <strong className="text-slate-700">{user?.email}</strong></li>
                    <li>Ask them to share a business folder with you</li>
                    <li><strong>Important:</strong> Open the shared folder in Google Drive first</li>
                    <li>Then click "Check for Access" below</li>
                  </ol>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => retryInit()} variant="outline" className="flex-1">
                    <Cloud className="w-4 h-4 mr-2" />
                    Check for Access
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => window.open('https://drive.google.com/drive/shared-with-me', '_blank')}
                  >
                    Open Drive
                  </Button>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeItem="dashboard" />
      
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-slate-600 mt-1">Overview of your receipts and transactions</p>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-8">
            <Button
              data-testid="quick-upload-receipt"
              onClick={() => navigate("/receipts")}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Receipt
            </Button>
            {isAdmin && (
            <>
            <Button
              data-testid="quick-import-statements"
              onClick={() => navigate("/bank-statements")}
              variant="outline"
            >
              <FileText className="w-4 h-4 mr-2" />
              Import Statement
            </Button>
            <Button
              data-testid="quick-matching"
              onClick={() => navigate("/matching")}
              variant="outline"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Match Items
            </Button>
            </>
            )}
          </div>

          {/* Stats Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Total Receipts</p>
                        <p className="text-3xl font-bold text-slate-900">{stats.totalReceipts}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center">
                        <Receipt className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Unmatched Receipts</p>
                        <p className={`text-3xl font-bold ${stats.unmatchedReceipts > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                          {stats.unmatchedReceipts}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-lg flex items-center justify-center">
                        <AlertCircle className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Total Transactions</p>
                        <p className="text-3xl font-bold text-slate-900">{stats.totalTransactions}</p>
                      </div>
                      <div className="w-12 h-12 bg-slate-100 text-slate-700 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Unmatched Transactions</p>
                        <p className={`text-3xl font-bold ${stats.unmatchedTransactions > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                          {stats.unmatchedTransactions}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-lg flex items-center justify-center">
                        <Link2 className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Month Summary - Admin Only */}
              {isAdmin && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <Card className="lg:col-span-2 bg-white border border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      This Month ({currentMonth})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Receipts</p>
                        <p className="text-2xl font-bold text-slate-900">{stats.monthReceiptsCount}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Total Spend</p>
                        <p className="text-2xl font-bold text-slate-900">£{stats.monthSpend.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">VAT Paid</p>
                        <p className="text-2xl font-bold text-emerald-600">£{stats.monthVat.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <Button
                        data-testid="view-vat-report"
                        variant="outline"
                        onClick={() => navigate("/vat-reports")}
                        className="w-full justify-center"
                      >
                        View Full VAT Report
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Storage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-6">
                      <div className="text-center">
                        <Cloud className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">All data stored in</p>
                        <p className="font-semibold text-slate-900">Google Drive</p>
                      </div>
                    </div>
                    <Button
                      data-testid="view-settings"
                      variant="outline"
                      onClick={() => navigate("/settings")}
                      className="w-full justify-center"
                    >
                      View Settings
                    </Button>
                  </CardContent>
                </Card>
              </div>
              )}

              {/* Recent Receipts */}
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Recent Receipts
                  </CardTitle>
                  <Button
                    data-testid="view-all-receipts"
                    variant="ghost"
                    onClick={() => navigate("/receipts")}
                  >
                    View All
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {recentReceipts.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <th className="py-3 px-4 text-left font-medium">Date</th>
                            <th className="py-3 px-4 text-left font-medium">Merchant</th>
                            <th className="py-3 px-4 text-right font-medium">Total</th>
                            <th className="py-3 px-4 text-right font-medium">VAT</th>
                            <th className="py-3 px-4 text-center font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentReceipts.map((receipt, index) => (
                            <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50">
                              <td className="py-3 px-4 text-sm text-slate-900">
                                {receipt.transactionDate?.slice(0, 10)}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                                {receipt.merchantName}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-900 text-right font-mono">
                                £{(receipt.totalAmount || 0).toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-900 text-right font-mono">
                                £{(receipt.vatAmount || 0).toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  receipt.isMatched
                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    : "bg-amber-50 text-amber-700 border border-amber-200"
                                }`}>
                                  {receipt.isMatched ? (
                                    <>
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Matched
                                    </>
                                  ) : (
                                    <>
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Unmatched
                                    </>
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No receipts yet</p>
                      <Button
                        className="mt-4"
                        onClick={() => navigate("/receipts")}
                      >
                        Upload Your First Receipt
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
